import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import BottomNav from './BottomNav';
import StatusOffline from './StatusOffline';
import Absen from './Absen';
import Lapor from './Lapor';
import Izin from './Izin';
import IzinFormPage from './IzinFormPage';
import SakitFormPage from './SakitFormPage';
import DinasFormPage from './DinasFormPage';
import Keuangan from './Keuangan';
import KeuanganFormPage from './KeuanganFormPage';
import Selesai from './Selesai';
import LaporScanPage from './LaporScanPage';
import HandoverTrackingPage from './HandoverTrackingPage';
import SecurityPage from './SecurityPage';
import MonitoringBbmPage from './MonitoringBbmPage';
import ChecklistKendaraanPage from './ChecklistKendaraanPage';
import RequestITPage from './RequestITPage';
import ATKPemesananPage from './ATKPemesananPage';
import Swal from 'sweetalert2';
import './App.css';
import LokasiStatus from './LokasiStatus';
import { SpeedInsights } from "@vercel/speed-insights/react";
import { motion } from 'framer-motion';
import UpdateIntroTour from './components/feature-tour/UpdateIntroTour';
import AbsenEdit from './AbsenEdit';
import { AnimatePresence } from "framer-motion";
import PageTransition from './PageTransition';
import PageTransitionBack from './PageTransitionBack';
import VersionLogModal from './components/VersionLogModal';
import PetunjukLoginModal from './components/PetunjukLoginModal';
import PrivacyPolicyModal from './components/PrivacyPolicyModal';
import { currentVersion } from './data/versionLog';
import { hasAgreedPrivacyPolicy } from './data/privacyPolicyData';
import { readHandovers, writeHandovers } from './utils/handoverStorage';
import RiwayatNotifikasiCard from './components/RiwayatNotifikasiCard';
import SupportITButton from './components/SupportITButton';
import { isPendingUang } from './utils/uangApproval';
import { Briefcase, TrendingUp, UserCheck, Store, MapPin, Phone, Mail } from 'lucide-react';

const LOCAL_ABSEN_SERVER_KEY = 'absen_server';
const LOCAL_ABSEN_KONFIRMASI_KEY = 'absen_konfirmasi';
const LOCAL_IJIN_KONFIRMASI_KEY = 'ijin_konfirmasi';
const LOCAL_IJIN_PENGAJUAN_KEY = 'ijin_pengajuan';
const LOCAL_UANG_PENGAJUAN_KASBON_KEY = 'uang_pengajuanKasbon';
const LOCAL_UANG_KONFIRMASI_POTONGAN_KEY = 'uang_konfirmasiPotongan';
const LOCAL_RIWAYAT_KEY = 'riwayat_absen';
const LOCAL_LOCATION_MONITOR_KEY = 'absen_location_monitor';
const LOCATION_STAY_MAX_DISTANCE_METERS = 180;
const UPDATE_REQUEST_TIMEOUT_MS = 8000;

const safeSetLocalStorage = (key, value) => {
  if (value === undefined || value === null) return false;
  const serialized = typeof value === 'string' ? value : JSON.stringify(value);
  try {
    localStorage.setItem(key, serialized);
    return true;
  } catch (error) {
    if (error.name === 'QuotaExceededError' || error.code === 22 || error.code === 1014) {
      console.warn(`[safeSetLocalStorage] QuotaExceededError untuk key "${key}". Mencoba pembersihan...`);
      try {
        localStorage.removeItem(key);
        localStorage.setItem(key, serialized);
        return true;
      } catch (retryError) {
        if (Array.isArray(value)) {
          try {
            const sliced = value.slice(-30);
            localStorage.setItem(key, JSON.stringify(sliced));
            console.warn(`[safeSetLocalStorage] Berhasil menyimpan fallback (${sliced.length} item) untuk key "${key}".`);
            return true;
          } catch (sliceError) {
            console.error(`[safeSetLocalStorage] Fallback juga gagal untuk key "${key}":`, sliceError);
          }
        }
      }
    } else {
      console.error(`[safeSetLocalStorage] Gagal menyimpan key "${key}":`, error);
    }
    return false;
  }
};

const fetchWithTimeout = (url, options = {}, timeoutMs = UPDATE_REQUEST_TIMEOUT_MS) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, { ...options, signal: controller.signal })
    .finally(() => clearTimeout(timer));
};

const calculateDistanceMeters = (lat1, lon1, lat2, lon2) => {
  const R = 6371e3;
  const phi1 = (lat1 * Math.PI) / 180;
  const phi2 = (lat2 * Math.PI) / 180;
  const deltaPhi = ((lat2 - lat1) * Math.PI) / 180;
  const deltaLambda = ((lon2 - lon1) * Math.PI) / 180;
  const a = Math.sin(deltaPhi / 2) ** 2 + Math.cos(phi1) * Math.cos(phi2) * Math.sin(deltaLambda / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const readJsonArray = (key) => {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const findServerAbsen = (monitor) => {
  const serverList = readJsonArray(LOCAL_ABSEN_SERVER_KEY);

  return serverList.find(item => {
    const tanggalMatch = item.tanggal === monitor.tanggal;
    const shiftMatch = !monitor.shift_id || !item.shift_id || String(item.shift_id) === String(monitor.shift_id);
    const masuk = item.jam_masuk || item.masuk || item.waktu_masuk || '';
    const masukMatch = !monitor.waktu || !masuk || String(masuk).substring(0, 5) === String(monitor.waktu).substring(0, 5);
    return tanggalMatch && shiftMatch && masukMatch && parseInt(item.absen_id || 18) === 18;
  });
};

const updateLocalInvalidAbsen = (monitor, distance) => {
  const durationLabel = monitor.duration_label || '1 jam';
  const markItem = (item) => {
    const tanggalMatch = item.tanggal === monitor.tanggal;
    const shiftMatch = !monitor.shift_id || !item.shift_id || String(item.shift_id) === String(monitor.shift_id);
    const waktuMatch = !monitor.waktu || !item.waktu || String(item.waktu).substring(0, 5) === String(monitor.waktu).substring(0, 5);

    if (!tanggalMatch || !shiftMatch || !waktuMatch) return item;

    return {
      ...item,
      status_validasi: 'perlu_verifikasi',
      log: item.log || 'keluar_lokasi',
      telat: Math.max(parseInt(item.telat) || 0, 11),
      uang_makan: 0,
      catatan_validasi: `Keluar dari lokasi absen sebelum ${durationLabel}. Jarak terdeteksi ${Math.round(distance)} meter dari ${monitor.office_name}.`,
    };
  };

  localStorage.setItem(LOCAL_RIWAYAT_KEY, JSON.stringify(readJsonArray(LOCAL_RIWAYAT_KEY).map(markItem)));
  localStorage.setItem(LOCAL_ABSEN_SERVER_KEY, JSON.stringify(readJsonArray(LOCAL_ABSEN_SERVER_KEY).map(markItem)));
};

const markInvalidOnServer = async (monitor) => {
  let serverItem = findServerAbsen(monitor);

  if (!serverItem?.id) {
    await fetchRiwayat();
    serverItem = findServerAbsen(monitor);
  }

  if (!serverItem?.id) {
    throw new Error('ID absen server belum ditemukan');
  }

  const res = await fetch(`https://jne-cilacap.com/slip/public/api/absen/tidak-valid/${serverItem.id}`, {
    method: 'POST',
    headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    throw new Error(data.message || 'Gagal menandai absen tidak valid');
  }

  return serverItem;
};

const isResignKaryawan = (karyawan, user) => {
  const status = String(karyawan?.status || user?.status || '').trim().toUpperCase();
  const active = String(karyawan?.active || user?.active || '').trim().toUpperCase();
  return status === 'RESIGN' || status.includes('RESIGN') || active === 'RESIGN';
};

const absenInfo = [
  { icon: "⏰", text: "Pelaporan lupa absen maximal H + 1", bg: "#E8F4FF", color: "#0077B6" },
  { icon: "🏁", text: "Toleransi lupa absen pulang 3 X", bg: "#EAFBF3", color: "#1B7F5C" },
  { icon: "📍", text: "Absen eror disesuaikan jam laporan", bg: "#F3EDFF", color: "#6A4C93" },
];

const agenInfo = [
  { icon: "💰", text: "Penyetoran uang PL maksimal H+2", bg: "#FEF3C7", color: "#B45309" },
  { icon: "📦", text: "Semua paket wajib tertempel resi", bg: "#E0F2FE", color: "#0369A1" },
  { icon: "⚖️", text: "Pastikan fisik & timbangan paket akurat", bg: "#DCFCE7", color: "#15803D" },
  { icon: "🏷️", text: "Gunakan flyer sesuai Service", bg: "#F3E8FF", color: "#7E22CE" },
  { icon: "🏪", text: "Jaga kebersihan & kerapihan counter agen", bg: "#FFF4E5", color: "#B26A00" },
];

const fiturInfo = [
  { iconClass: "fas fa-boxes", text: "Fitur Pesan ATK", bg: "#fdf4ff", color: "#a855f7" },
  { iconClass: "fas fa-user-gear", text: "Fitur Bantuan IT", bg: "#e0e7ff", color: "#4f46e5" },
  { iconClass: "fas fa-handshake", text: "Fitur Handover & Titipan HO", bg: "#eff6ff", color: "#2563eb" },
  { iconClass: "fas fa-desktop", text: "Fitur Manajemen Asset", bg: "#f0fdfa", color: "#0d9488" },
  { iconClass: "fas fa-gas-pump", text: "Fitur Monitoring BBM", bg: "#fff7ed", color: "#f97316" },
  { iconClass: "fas fa-clipboard-check", text: "Fitur Checklist Kendaraan", bg: "#fdf2f8", color: "#ec4899" },
  { iconClass: "fas fa-broom", text: "Fitur Monitoring Tugas OB", bg: "#fffbeb", color: "#d97706" },
  { iconClass: "fas fa-shield-alt", text: "Fitur Security & Patroli", bg: "#f5f3ff", color: "#7c3aed" },
];

const FiturTickerBanner = () => {
  const [index, setIndex] = useState(0);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setAnimating(true);
      setTimeout(() => {
        setIndex((prev) => (prev + 1) % fiturInfo.length);
        setAnimating(false);
      }, 450);
    }, 2600);

    return () => clearInterval(interval);
  }, []);

  const current = fiturInfo[index];

  return (
    <div
      style={{
        overflow: "hidden",
        width: "100%",
        maxWidth: 450,
        margin: '12px auto 14px',
        display: "flex",
        justifyContent: "center",
        minHeight: 42,
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          width: '100%',
          padding: "8px 14px",
          borderRadius: 16,
          background: current.bg,
          color: current.color,
          fontWeight: 600,
          fontSize: 12.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
          border: '1px solid rgba(0, 0, 0, 0.06)',

          /* ANIMATION */
          opacity: animating ? 0 : 1,
          filter: animating ? "blur(6px)" : "blur(0)",
          transform: animating
            ? "translateY(-40%)"
            : "translateY(0)",
          transition:
            "opacity 0.45s ease, transform 0.45s ease, filter 0.45s ease",
        }}
      >
        <i className={current.iconClass} style={{ fontSize: 16, color: current.color }} />
        <span>{current.text}</span>
      </div>
    </div>
  );
};

const getPeriod = () => {
  const now = new Date();
  let month = now.getMonth();
  const year = now.getFullYear();
  if (now.getDate() >= 21) {
    month += 1;
    if (month > 11) {
      month = 0;
      return `${new Date(year + 1, month).toLocaleString('en-US', { month: 'long' })} ${year + 1}`;
    }
  }
  return `${new Date(year, month).toLocaleString('en-US', { month: 'long' })} ${year}`;
};

// Fungsi untuk menghasilkan UUID acak (36 karakter)
const generateUUID = () => {
  const chars = '0123456789abcdef';
  const uuid = Array(36).fill('').map((_, i) => {
    if (i === 8 || i === 13 || i === 18 || i === 23) return '-';
    if (i === 14) return '4'; // UUID versi 4
    if (i === 19) return chars[(Math.random() * 4 | 0) + 8]; // Varian RFC4122
    return chars[Math.random() * 16 | 0];
  }).join('');
  return uuid;
};

const fetchRiwayat = async () => {
  try {
    const isAgen = localStorage.getItem('is_agen') === 'true';
    const karyawan = JSON.parse(localStorage.getItem('data_karyawan') || '{}');
    const user = JSON.parse(localStorage.getItem('data_user') || '{}');

    // Untuk akun agen / non-karyawan, tidak butuh modul absensi karyawan, tapi BUTUH karyawan_pengganti_list (untuk handover / titip barang)
    if (isAgen || karyawan.is_agen || user.role === 'agen' || !String(karyawan.nik || user.username || '').toUpperCase().startsWith('CXP')) {
      try {
        const res = await fetchWithTimeout('https://jne-cilacap.com/slip/public/api/riwayat-absen/108');
        const json = await res.json();
        if (json.success && Array.isArray(json.karyawan_pengganti_list)) {
          safeSetLocalStorage('karyawan_pengganti_list', json.karyawan_pengganti_list);
        }
      } catch (err) {
        console.warn('Gagal ambil data karyawan pengganti untuk agen:', err);
      }
      return true; // Agen lewati sisa modul absensi karyawan
    }

    if (!karyawan.id) return false;

    const res = await fetchWithTimeout(
      `https://jne-cilacap.com/slip/public/api/riwayat-absen/${karyawan.id}?periode=${encodeURIComponent(getPeriod())}`
    );
    const json = await res.json();
    if (json.success) {
      if (isResignKaryawan(json.data_karyawan, json.data_user)) {
        localStorage.clear();
        window.location.href = '/';
        return false;
      }
      safeSetLocalStorage(LOCAL_ABSEN_SERVER_KEY, json.data);
      safeSetLocalStorage(LOCAL_ABSEN_KONFIRMASI_KEY, json.data_konfirmasi);
      safeSetLocalStorage(LOCAL_IJIN_KONFIRMASI_KEY, json.ijin_konfirmasi);
      safeSetLocalStorage(LOCAL_IJIN_PENGAJUAN_KEY, json.ijin_pengajuan);
      safeSetLocalStorage(LOCAL_UANG_PENGAJUAN_KASBON_KEY, json.uang_pengajuanKasbon);

      // Hanya simpan item uang yang masih pending konfirmasi untuk menghemat kuota localStorage (~10MB -> ~600KB)
      // Data lama yang sudah di-approve berisi tanda tangan base64 besar yang mencapai ~10MB pada akun HC/HRD
      const rawUang = Array.isArray(json.uang_konfirmasiPotongan) ? json.uang_konfirmasiPotongan : [];
      const pendingUang = rawUang.filter(isPendingUang);
      safeSetLocalStorage(LOCAL_UANG_KONFIRMASI_POTONGAN_KEY, pendingUang);

      safeSetLocalStorage('data_shift', json.data_shift);
      safeSetLocalStorage('data_karyawan', json.data_karyawan);
      safeSetLocalStorage('data_user', json.data_user);
      safeSetLocalStorage('data_lokasi_absen', json.data_lokasi_absen);
      safeSetLocalStorage('jenis_ijin_list', json.jenis_ijin_list);
      safeSetLocalStorage('jenis_izin_list', json.jenis_izin_list);
      if (json.menu_setting !== undefined && json.menu_setting !== null) {
        safeSetLocalStorage('menu_setting', json.menu_setting);
      }
      safeSetLocalStorage('karyawan_pengganti_list', json.karyawan_pengganti_list);
      return true;
    }
    return false;
  } catch (e) {
    console.error('Gagal ambil riwayat:', e);
    return false;
  }
};

// Fungsi ambil ulang data riwayat absen dari server
const fetchLaporLupa = async () => {
  try {
    const user = JSON.parse(localStorage.getItem('data_user') || '{}');

    if (user.role !== 'hrd') return true;

    const res = await fetchWithTimeout(
      `https://jne-cilacap.com/slip/public/api/lapor-lupaAbsen`
    );

    const json = await res.json();

    if (json.success) {
      safeSetLocalStorage(
        'lapor_lupa_absen',
        json.lapor_lupa_absen
      );
      return true;
    }
    return false;
  } catch (e) {
    console.error('Gagal ambil data laporan:', e);
    return false;
  }
};

const hapusRiwayatTerakhir = () => {
  try {
    const raw = localStorage.getItem('riwayat_absen');
    if (!raw) return;

    const riwayat = JSON.parse(raw);

    // Kalau kosong, tidak perlu apa-apa
    if (!Array.isArray(riwayat) || riwayat.length === 0) return;

    // Hapus item terakhir
    riwayat.pop();

    // Simpan hasilnya
    localStorage.setItem('riwayat_absen', JSON.stringify(riwayat));

    console.log("Berhasil hapus item terakhir. Sisa data:", riwayat.length);

    // Optional reload
    window.location.reload();
  } catch (e) {
    console.error("Gagal hapus item terakhir:", e);
  }
};

// ==================== HomeMenus Component ====================
const normalizeJabatan = (value = '') => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[.\-_\/]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const hasAllowedJabatan = (jabatan, allowedList) => {
  const normalized = normalizeJabatan(jabatan);
  return allowedList.some((role) => {
    if (!role) return false;
    const roleKey = normalizeJabatan(role);
    return normalized === roleKey || normalized.includes(roleKey);
  });
};

const HomeMenus = ({ dataKaryawan, badgeOB, badgeTitipan, navigate }) => {
  const [showAll, setShowAll] = useState(false);
  const jabatan = normalizeJabatan(dataKaryawan?.jabatan);

  const handleObClick = () => {
    const isObAuthorized = hasAllowedJabatan(jabatan, ['office boy', 'human capital', 'general affair']);

    if (isObAuthorized) {
      navigate('/lapor', { state: { activeTab: 'ob' } });
    } else {
      Swal.fire({
        icon: 'warning',
        title: 'Akses Ditolak',
        text: 'Menu ini hanya dapat diakses oleh Office Boy, Human Capital, atau General Affair.',
        confirmButtonText: 'Mengerti',
        confirmButtonColor: '#3085d6',
      });
    }
  };

  const handleRestrictedMenuClick = (route, allowedList, message) => {
    if (hasAllowedJabatan(jabatan, allowedList)) {
      navigate(route);
      return;
    }

    Swal.fire({
      icon: 'warning',
      title: 'Akses Ditolak',
      text: message,
      confirmButtonText: 'Mengerti',
      confirmButtonColor: '#3085d6',
    });
  };

  const handleMenuClick = (tab) => {
    navigate('/lapor', { state: { activeTab: tab } });
  };

  const badgeStyle = {
    position: 'absolute',
    top: '-4px',
    right: '-4px',
    backgroundColor: '#ff3b30',
    color: '#fff',
    borderRadius: '12px',
    padding: '2px 6px',
    fontSize: '10px',
    fontWeight: 'bold',
    boxShadow: '0 2px 5px rgba(255, 59, 48, 0.4)',
    border: '1.5px solid #fff',
    zIndex: 10,
  };

  const isFemaleUser = (() => {
    try {
      const u = JSON.parse(localStorage.getItem('data_user') || '{}');
      const k = JSON.parse(localStorage.getItem('data_karyawan') || '{}');
      const g = (u.gender || u.jenis_kelamin || k.gender || k.jenis_kelamin || '').toString().trim().toLowerCase();
      return g === 'female' || g === 'perempuan' || g === 'wanita' || g === 'p';
    } catch {
      return false;
    }
  })();

  const menuList = [
    {
      id: 'atk-pemesanan',
      label: 'Pesan ATK',
      icon: 'fas fa-boxes',
      circleBg: isFemaleUser ? '#f1f5f9' : '#fdf4ff',
      iconColor: isFemaleUser ? '#94a3b8' : '#a855f7',
      onClick: isFemaleUser
        ? () => Swal.fire({
          icon: 'warning',
          title: 'Akses Dibatasi',
          text: 'Menu pemesanan ATK tidak tersedia untuk akun Anda.',
          confirmButtonText: 'Mengerti',
          customClass: { confirmButton: 'btn btn-primary' },
        })
        : () => navigate('/atk-pemesanan'),
    },
    {
      id: 'request-it',
      label: 'Bantuan IT',
      icon: 'fas fa-user-gear',
      circleBg: '#e0e7ff',
      iconColor: '#4f46e5',
      onClick: () => navigate('/request-it'),
    },
    {
      id: 'ho',
      label: 'Handover',
      icon: 'fas fa-handshake',
      circleBg: '#eff6ff',
      iconColor: '#2563eb',
      badge: badgeTitipan,
      onClick: () => handleMenuClick('ho'),
    },
    {
      id: 'asset',
      label: 'Asset',
      icon: 'fas fa-desktop',
      circleBg: '#f0fdfa',
      iconColor: '#0d9488',
      onClick: () => handleMenuClick('asset'),
    },
    {
      id: 'monitoring-bbm',
      label: 'Monitoring',
      icon: 'fas fa-gas-pump',
      circleBg: '#fff7ed',
      iconColor: '#f97316',
      onClick: () => handleRestrictedMenuClick('/monitoring-bbm', ['security', 'human capital', 'general affair', 'it', 'koordinator', 'jr supervisor'], 'Menu ini hanya dapat diakses oleh Security, Human Capital, General Affair, IT, Koordinator, atau Jr Supervisor.'),
    },
    {
      id: 'checklist-kendaraan',
      label: 'Kendaraan',
      icon: 'fas fa-clipboard-check',
      circleBg: '#fdf2f8',
      iconColor: '#ec4899',
      onClick: () => handleRestrictedMenuClick('/checklist-kendaraan', ['security', 'human capital', 'general affair', 'it', 'koordinator', 'jr supervisor'], 'Menu ini hanya dapat diakses oleh Security, Human Capital, General Affair, IT, Koordinator, atau Jr Supervisor.'),
    },
    {
      id: 'ob',
      label: 'Tugas OB',
      icon: 'fas fa-broom',
      circleBg: '#fffbeb',
      iconColor: '#d97706',
      badge: badgeOB,
      onClick: handleObClick,
    },
    {
      id: 'security',
      label: 'Security',
      icon: 'fas fa-shield-alt',
      circleBg: '#f5f3ff',
      iconColor: '#7c3aed',
      onClick: () => handleRestrictedMenuClick('/security', ['security', 'human capital', 'it'], 'Menu ini hanya dapat diakses oleh Security, Human Capital, atau IT.'),
    },
  ];

  const visibleMenus = showAll ? menuList : menuList.slice(0, 4);

  return (
    <div style={{
      backgroundColor: '#ffffff',
      padding: '18px 16px 14px',
      borderRadius: 24,
      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)',
      border: '1px solid #cbd5e1',
      maxWidth: 450,
      margin: '0 auto 20px',
      boxSizing: 'border-box',
    }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
        gap: '16px 8px',
        alignItems: 'start',
        width: '100%',
        boxSizing: 'border-box',
      }}>
        {visibleMenus.map((menu) => (
          <div
            key={menu.id}
            id={menu.id === 'request-it' ? 'menu-it-request' : menu.id === 'atk-pemesanan' ? 'menu-atk-pemesanan' : undefined}
            onClick={menu.onClick}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              cursor: 'pointer',
              userSelect: 'none',
              transition: 'transform 0.15s ease',
            }}
            onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.93)'}
            onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            <div style={{
              position: 'relative',
              width: 52,
              height: 52,
              borderRadius: '50%',
              backgroundColor: menu.circleBg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 6,
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              border: '1px solid rgba(0,0,0,0.07)',
            }}>
              {menu.badge > 0 && (
                <div style={badgeStyle}>
                  {menu.badge > 99 ? '99+' : menu.badge}
                </div>
              )}
              <i className={menu.icon} style={{ fontSize: '20px', color: menu.iconColor }} />
            </div>
            <span style={{
              fontSize: '11px',
              fontWeight: '600',
              color: '#334155',
              textAlign: 'center',
              lineHeight: 1.2,
              wordBreak: 'break-word',
            }}>
              {menu.label}
            </span>
          </div>
        ))}
      </div>

      {/* Pill Button View More / Less */}
      <div
        onClick={() => setShowAll(!showAll)}
        style={{
          marginTop: 16,
          backgroundColor: '#f4fbf7',
          borderRadius: 25,
          padding: '10px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          cursor: 'pointer',
          border: '1px solid #e1f5eb',
          transition: 'all 0.2s ease',
        }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e8f7f0'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f4fbf7'}
      >
        <i className="fas fa-th-large" style={{ color: '#10b981', fontSize: 13 }} />
        <span style={{ color: '#10b981', fontSize: 13, fontWeight: '700' }}>
          {showAll ? 'Sembunyikan Fitur' : 'Lihat Semua Fitur'}
        </span>
      </div>
    </div>
  );
};

const Home = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [nik, setNik] = useState('');
  const status = 'Absen'; // hanya baca, tidak set
  const [jarak, setJarak] = useState(null);
  const [kantorTerdekat, setKantorTerdekat] = useState('');
  const [loading, setLoading] = useState(false);
  const [dataKaryawan, setDataKaryawan] = useState(null);
  const [isEnabled, setIsEnabled] = useState(false); // State baru untuk BottomNav
  const [showUpdateTip, setShowUpdateTip] = useState(true); // default: tampil
  const [showBadge, setShowBadge] = useState(false);
  const [hasUpdate, setHasUpdate] = useState(false);
  const [latestMessage, setLatestMessage] = useState("");
  const [index, setIndex] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [badgeOB, setBadgeOB] = useState(0);
  const [badgeTitipan, setBadgeTitipan] = useState(0);
  const [showVersionLog, setShowVersionLog] = useState(false);
  const [showPetunjukLogin, setShowPetunjukLogin] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [isPrivacyMandatory, setIsPrivacyMandatory] = useState(false);

  useEffect(() => {
    const updateBadges = () => {
      const ob = parseInt(localStorage.getItem('badge_ob') || '0') || 0;

      try {
        const dataTitipan = readHandovers();
        const karyawan = JSON.parse(localStorage.getItem('data_karyawan') || '{}');
        const myId = String(karyawan.id);
        const countTitipan = dataTitipan.filter(t =>
          String(t.recipient_id) === myId &&
          String(t.current_holder_id) !== myId &&
          String(t.status).toLowerCase() !== 'selesai'
        ).length;
        setBadgeTitipan(countTitipan);
        localStorage.setItem('badge_titipan', String(countTitipan));
      } catch (e) {
        setBadgeTitipan(0);
      }

      setBadgeOB(ob);
    };

    updateBadges();

    window.addEventListener('storage', updateBadges);
    window.addEventListener('badgeUpdate', updateBadges);
    window.addEventListener('ob-tasks-updated', updateBadges);

    const interval = setInterval(updateBadges, 3000);

    return () => {
      window.removeEventListener('storage', updateBadges);
      window.removeEventListener('badgeUpdate', updateBadges);
      window.removeEventListener('ob-tasks-updated', updateBadges);
      clearInterval(interval);
    };
  }, []);

  const isAgenUser = Boolean(dataKaryawan?.is_agen || location.pathname === '/agen');
  const activeInfoList = isAgenUser ? agenInfo : absenInfo;

  useEffect(() => {
    const interval = setInterval(() => {
      setAnimating(true);

      setTimeout(() => {
        setIndex((prev) => (prev + 1) % activeInfoList.length);
        setAnimating(false);
      }, 450);
    }, 2800);

    return () => clearInterval(interval);
  }, [activeInfoList.length]);

  useEffect(() => {
    const seen = localStorage.getItem(STORAGE_BADGE);
    if (!seen) {
      setShowBadge(true);
    }
  }, []);

  const STORAGE_BADGE = `hasSeenUpdateBadge_v${currentVersion}`; // ganti versi tiap update

  const clearCache = async () => {
    // 🔥 Swal dengan progress indeterminite (lingkaran muter) + teks berubah

    const swalInstance = Swal.fire({
      title: 'Sedang Mengupdate...',
      html: `
      <div style="text-align:left; font-size:14px; margin-top:10px;">
        <div>Memeriksa versi aplikasi...</div>
        <div>📡 Mengambil data terbaru dari server...</div>
        <div>🔄 Memperbarui tampilan...</div>
      </div>
    `,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading(); // lingkaran loading
      },
    });

    // sembunyikan badge
    setShowBadge(false);

    // simpan ke localStorage biar tidak muncul lagi
    localStorage.setItem(STORAGE_BADGE, "true");

    try {
      // 0. Cek UUID di localstorage dan cocokan dengan server
      const localDataUserStr = localStorage.getItem('data_user');
      const localDataKaryawanStr = localStorage.getItem('data_karyawan');
      const localDataUser = localDataUserStr ? JSON.parse(localDataUserStr) : {};
      const localDataKaryawan = localDataKaryawanStr ? JSON.parse(localDataKaryawanStr) : {};
      const currentNik = localDataKaryawan.nik || localDataUser.username || localDataUser.nik || '';
      const isAgenUser = !!localDataKaryawan.is_agen || localDataUser.role === 'agen' || localStorage.getItem('is_agen') === 'true' || !String(currentNik).toUpperCase().startsWith('CXP');

      if (localDataUserStr && localDataKaryawanStr) {
        // Ambil dari device_uuid terlebih dahulu, fallback ke data_user.uuid
        let localUuid = localStorage.getItem('device_uuid') || localDataUser.uuid;

        // 1. Tambahkan uuid baru ke localstorage jika memang belum ada
        if (!localUuid && currentNik) {
          localUuid = generateUUID();
          localDataUser.uuid = localUuid;
          localStorage.setItem('data_user', JSON.stringify(localDataUser));
          localStorage.setItem('device_uuid', localUuid); // Simpan secara mandiri
        } else if (localUuid) {
          // Pastikan UUID yang sudah ada tersimpan utuh di kedua tempat
          localDataUser.uuid = localUuid;
          localStorage.setItem('data_user', JSON.stringify(localDataUser));
          localStorage.setItem('device_uuid', localUuid);
        }

        // 2. Cocokkan dengan data_user.device_id
        if (localUuid && currentNik) {
          Swal.update({
            html: `
            <div style="text-align:left; font-size:15px; margin-top:10px;">
              <div>${isAgenUser ? '📡 Memperbarui sesi akun agen...' : '🔐 Memverifikasi sesi device Anda...'}</div>
            </div>
            `,
          });

          try {
            const verifUrl = isAgenUser
              ? 'https://jne-cilacap.com/slip/public/api/cek-debitur'
              : 'https://jne-cilacap.com/slip/public/api/cek-nik';
            const verifPayload = isAgenUser
              ? { username: currentNik, no_debitur: currentNik, user_name: localDataKaryawan.user_name || localDataUser.user_name || '' }
              : { nik: currentNik };

            const verifResponse = await axios.post(verifUrl, verifPayload, { timeout: UPDATE_REQUEST_TIMEOUT_MS });
            const dataServer = verifResponse.data;

            if (dataServer && isResignKaryawan(dataServer.data_karyawan, dataServer.data_user)) {
              await Swal.fire({
                icon: 'error',
                title: 'Gagal!',
                text: '❌ Karyawan sudah resign.',
                confirmButtonText: 'OK',
                customClass: { confirmButton: 'btn btn-primary' }
              });
              localStorage.clear();
              window.location.href = '/';
              return;
            }

            if ((dataServer.valid || dataServer.success) && dataServer.data_user) {
              const serverDeviceId = dataServer.data_user.device_id;

              if (dataServer.menu_setting !== undefined && dataServer.menu_setting !== null) {
                localStorage.setItem('menu_setting', JSON.stringify(dataServer.menu_setting));
              }

              if (isAgenUser) {
                // Untuk agen: Hilangkan validasi device ID & update data profil agen dari server
                setIsEnabled(true);
                if (dataServer.data_karyawan) {
                  const updatedKaryawan = { ...dataServer.data_karyawan, is_agen: true, device_id: localUuid };
                  localStorage.setItem('data_karyawan', JSON.stringify(updatedKaryawan));
                  setDataKaryawan(updatedKaryawan);
                }
                if (dataServer.data_user) {
                  const updatedUser = { ...dataServer.data_user, is_agen: true, device_id: localUuid, uuid: localUuid };
                  localStorage.setItem('data_user', JSON.stringify(updatedUser));
                }

                // Ambil daftar karyawan_pengganti_list untuk agen jika belum ada
                fetchRiwayat().catch(() => { });

                // Sinkronkan UUID terbaru ke server jika diperlukan tanpa memblokir
                if (!serverDeviceId || serverDeviceId !== localUuid) {
                  try {
                    await axios.post('https://jne-cilacap.com/slip/public/api/debitur/update-uuid', {
                      username: currentNik,
                      nik: currentNik,
                      uuid: localUuid
                    }, { timeout: UPDATE_REQUEST_TIMEOUT_MS });
                  } catch (err) {
                    console.warn('Gagal sinkronisasi uuid agen:', err);
                  }
                }
              } else if (!serverDeviceId || serverDeviceId === "") {
                // Jika device_id kosong, update dengan data uuid yang di localstorage
                try {
                  const updateUrl = 'https://jne-cilacap.com/slip/public/api/update-uuid';
                  await axios.post(updateUrl, {
                    nik: currentNik,
                    username: currentNik,
                    uuid: localUuid
                  }, { timeout: UPDATE_REQUEST_TIMEOUT_MS });
                  // Lalu tampilkan navbarBottom
                  setIsEnabled(true);
                } catch (err) {
                  console.error('Gagal update device_id', err);
                }

              } else if (serverDeviceId === localUuid) {
                // Jika datanya sama munculkan navbarbottom
                setIsEnabled(true);
              } else {
                // Jika tidak sama munculkan peringatan
                await Swal.fire({
                  icon: 'warning',
                  title: 'Device ID Tidak Dikenali',
                  text: 'Untuk mengurangi penyalahgunaan absensi, sistem hanya mengizinkan 1 perangkat/browser per karyawan. Device ID baru akan di generate ulang saat validasi, silakan hubungi tim IT untuk reset Device ID yang lama',
                  confirmButtonText: 'OK',
                  customClass: { confirmButton: 'btn btn-primary' }
                });
                setIsEnabled(false);
                return; // Stop proses update lebih lanjut
              }
            }
          } catch (e) {
            console.error('Gagal memverifikasi device_id:', e);
          }
        }
      }

      // 1. Backup data localStorage
      const backup = {
        absen_server: localStorage.getItem('absen_server'),
        data_shift: localStorage.getItem('data_shift'),
        data_karyawan: localStorage.getItem('data_karyawan'),
        data_user: localStorage.getItem('data_user'),
        data_lokasi_absen: localStorage.getItem('data_lokasi_absen'),
        jenis_ijin_list: localStorage.getItem('jenis_ijin_list'),
        jenis_izin_list: localStorage.getItem('jenis_izin_list'),
        menu_setting: localStorage.getItem('menu_setting'),
        ijin_konfirmasi: localStorage.getItem('ijin_konfirmasi'),
        ijin_pengajuan: localStorage.getItem('ijin_pengajuan'),
        uang_pengajuanKasbon: localStorage.getItem('uang_pengajuanKasbon'),
        uang_konfirmasiPotongan: localStorage.getItem('uang_konfirmasiPotongan'),
        karyawan_pengganti_list: localStorage.getItem('karyawan_pengganti_list'),
      };

      Swal.update({
        html: `
        <div style="text-align:left; font-size:15px; margin-top:10px;">
          <div>📡 Mengambil data terbaru dari server...</div>
        </div>
      `,
      });

      // 2. Coba fetch data baru (untuk agen tidak perlu fetch data absen)
      let fetchSukses = false;
      try {
        if (isAgenUser) {
          // Agen tidak ada modul absensi, tapi tetap perbarui karyawan_pengganti_list
          fetchSukses = await fetchRiwayat();
        } else {
          const [riwayatSukses] = await Promise.all([fetchRiwayat(), fetchLaporLupa()]);
          fetchSukses = riwayatSukses;
        }
      } catch (e) {
        console.error('Fetch gagal:', e);
      }

      // 3. Jika fetch gagal → kembalikan data (cache & SW TETAP ADA)
      if (!fetchSukses) {
        Object.keys(backup).forEach(key => {
          if (backup[key] !== null) safeSetLocalStorage(key, backup[key]);
        });

        await Swal.fire({
          icon: 'warning',
          title: 'Offline / Server Down',
          text: isAgenUser
            ? 'Tenang, data lama masih dipakai. Silakan coba lagi perbarui data saat sistem sudah normal.'
            : 'Tenang, data lama masih dipakai. Anda tetap bisa absen lewat menu Absen. Silakan coba lagi perbarui data saat sistem sudah normal.',
          confirmButtonText: 'OK',
        });
        return;
      }

      // 4. Fetch sukses → baru hapus cache + service worker
      Swal.update({
        html: `
        <div style="text-align:left; font-size:14px; margin-top:10px;">
          <div>Memeriksa pembaruan aplikasi...</div>
          <div>Menyimpan data terbaru...</div>
          <div>Memperbarui tampilan...</div>
        </div>
      `,
      });

      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.allSettled(regs.map(reg => Promise.race([
          reg.update(),
          new Promise(resolve => setTimeout(resolve, 3000)),
        ])));
      }

      // Trigger badge & OB tasks refresh so Menu Lapor updates immediately
      try {
        window.dispatchEvent(new Event('badgeUpdate'));
        window.dispatchEvent(new Event('ob-tasks-updated'));
      } catch (e) {
        console.warn('Unable to dispatch badgeUpdate/ob-tasks-updated events', e);
      }

      // 5. Reload
      await Swal.fire({
        icon: 'success',
        title: 'Berhasil Diperbarui!',
        text: 'Aplikasi akan reload dalam 2 detik...',
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });

      window.location.reload();

    } catch (err) {
      console.error('Error fatal:', err);
      await Swal.fire({
        icon: 'error',
        title: 'Gagal Update',
        text: 'Terjadi kesalahan. Data lama tetap digunakan.',
        confirmButtonText: 'OK',
      });
    }

  };

  const kantorLocations = [
    { name: "JNE Cilacap", latitude: -7.703791784344155, longitude: 109.02706575208764 },
    { name: "KP Tritih", latitude: -7.6436984059273625, longitude: 109.03945539274153 },
    { name: "KP Jeruklegi", latitude: -7.622739973947992, longitude: 109.02027662866101 },
    { name: "KP Jeruklegi", latitude: -7.623527514340614, longitude: 109.01967888209312 },
    { name: "Pickup Jambusari", latitude: -7.570450003947906, longitude: 109.0257079820926 },
    { name: "KP Bantarsari", latitude: -7.553852092912492, longitude: 108.88562791441512 },
    { name: "KP Bantarsari", latitude: -7.556101357393882, longitude: 108.88581543810471 },
    { name: "KP Kawunganten", latitude: -7.597121637889708, longitude: 108.92088169558562 },
    { name: "KP Kawunganten", latitude: -7.596978029880988, longitude: 108.92066665149882 },
    { name: "KP Sidareja", latitude: -7.485411168876285, longitude: 108.8010667954 },
    { name: "KP Cimanggu", latitude: -7.356556746125122, longitude: 108.84221591667256 },
    { name: "KP Majenang", latitude: -7.298644648842441, longitude: 108.76225156021464 },
    { name: "KP Majenang", latitude: -7.299063, longitude: 108.761253 },
    { name: "KP Kesugihan", latitude: -7.6200721020375255, longitude: 109.11965779985853 },
    { name: "KP Sidareja 1", latitude: -7.481438057145171, longitude: 108.79634340518692 },
    { name: "Rumah Ibu", latitude: -7.514161451335894, longitude: 109.06080762883563 }
  ];

  const radiusDiterima = 100; // meter

  const hitungJarakMeter = (lat1, lon1, lat2, lon2) => {
    const R = 6371e3;
    const toRad = (x) => (x * Math.PI) / 180;
    const φ1 = toRad(lat1);
    const φ2 = toRad(lat2);
    const Δφ = toRad(lat2 - lat1);
    const Δλ = toRad(lon2 - lon1);

    const a =
      Math.sin(Δφ / 2) ** 2 +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;

    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  useEffect(() => {
    try {
      const stored = localStorage.getItem('data_karyawan');
      const lokasi = localStorage.getItem('lokasi_terdeteksi');
      if (stored) {
        const parsedKaryawan = JSON.parse(stored);
        const storedUser = localStorage.getItem('data_user');
        const parsedUser = storedUser ? JSON.parse(storedUser) : null;
        if (isResignKaryawan(parsedKaryawan, parsedUser)) {
          localStorage.clear();
          setDataKaryawan(null);
          setIsEnabled(false);
          return;
        }
        setDataKaryawan(parsedKaryawan);

        // Pengecekan sinkronisasi device untuk menampilkan BottomNav
        const localDataUserStr = localStorage.getItem('data_user');
        const deviceUuid = localStorage.getItem('device_uuid');

        let isValidDevice = false;
        if (localDataUserStr && deviceUuid) {
          const localDataUser = JSON.parse(localDataUserStr);
          if (localDataUser.device_id === deviceUuid) {
            isValidDevice = true;
          }
        }

        const isAgenUser = !!parsedKaryawan.is_agen || localStorage.getItem('is_agen') === 'true' || parsedKaryawan.role === 'agen' || !String(parsedKaryawan.nik || '').toUpperCase().startsWith('CXP');
        setIsEnabled(isAgenUser || (!!lokasi && isValidDevice));
      }
    } catch (e) {
      console.warn('Data karyawan tidak valid:', e);
    }
  }, []);

  const handleValidasiNIKdanLokasi = async (bypassPrivacy = false) => {
    const inputVal = (nik || '').trim();
    if (!inputVal) {
      Swal.fire({
        icon: 'error',
        title: 'Peringatan!',
        text: '❌ Harap isi NIK atau Kode. Debitur terlebih dahulu.',
        confirmButtonText: 'OK',
        customClass: { confirmButton: 'btn btn-primary' },
      });
      return;
    }

    // Wajib boolean true murni agar event object dari onClick tidak mem-bypass dialog
    if (bypassPrivacy !== true) {
      setIsPrivacyMandatory(true);
      setShowPrivacyModal(true);
      return;
    }

    const isCxp = inputVal.toUpperCase().startsWith('CXP');

    // =========================================================================
    // PERCABANGAN 1: VALIDASI AGEN / DEBITUR (JIKA INPUT TANPA AWALAN CXP)
    // =========================================================================
    if (!isCxp) {
      // Pre-fetch nama agen berdasarkan Kode. Debitur sebelum dialog verifikasi
      let previewNamaAgen = '';
      let previewStatusAgen = '';
      try {
        Swal.fire({
          title: 'Mengambil Data...',
          text: '🔍 Mencari data agen...',
          allowOutsideClick: false,
          didOpen: () => { Swal.showLoading(); },
        });
        const preRes = await axios.post('https://jne-cilacap.com/slip/public/api/info-debitur', {
          no_debitur: inputVal,
        }, { timeout: 8000 });
        const preData = preRes.data;
        if (preData.found && preData.nama_agen) {
          previewNamaAgen = preData.nama_agen;
          previewStatusAgen = preData.status || '';
        }
        Swal.close();
      } catch (_) {
        Swal.close();
      }

      const { value: usernameHybrid, isDismissed } = await Swal.fire({
        title: 'Verifikasi Agen JNE',
        html: `
          <div style="font-size: 13px; color: #475569; margin-bottom: 10px; line-height: 1.5;">
            Kode. Debitur: <strong style="color: #003087; font-size: 15px;">${inputVal}</strong>
          </div>
          ${previewNamaAgen ? `
          <div style="background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 8px; padding: 8px 12px; margin-bottom: 10px; text-align: left;">
            <div style="font-size: 11px; color: #64748b; margin-bottom: 2px;">Nama Agen Terdeteksi</div>
            <div style="font-size: 14px; font-weight: 700; color: #1e40af;">${previewNamaAgen}</div>
            ${previewStatusAgen ? `<div style="font-size: 11px; color: ${previewStatusAgen === 'Aktif' ? '#16a34a' : '#dc2626'}; margin-top: 2px;">● ${previewStatusAgen}</div>` : ''}
          </div>
          ` : ''}
          <div style="font-size: 13px; color: #64748b; margin-bottom: 6px;">
            Silakan masukkan <strong>Username Hybrid</strong> Anda:
          </div>
        `,
        input: 'text',
        inputPlaceholder: 'Contoh: AHMADCXP',
        inputAttributes: {
          autocapitalize: 'off',
          autocorrect: 'off',
        },
        showCancelButton: true,
        confirmButtonText: 'Validasi & Masuk',
        cancelButtonText: 'Batal',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-secondary',
        },
        inputValidator: (val) => {
          if (!val || !val.trim()) {
            return 'Username Hybrid tidak boleh kosong!';
          }
        }
      });

      if (isDismissed || !usernameHybrid) {
        return;
      }

      setLoading(true);
      Swal.fire({
        title: 'Memvalidasi...',
        text: '🔄 Memvalidasi Kode. Debitur & Username Hybrid...',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        const response = await axios.post('https://jne-cilacap.com/slip/public/api/cek-debitur', {
          username: inputVal,
          no_debitur: inputVal,
          user_name: usernameHybrid.trim(),
        });
        const data = response.data;

        if (!data.valid && !data.success) {
          throw new Error(data.message || 'Data debitur atau Username Hybrid tidak cocok.');
        }

        const newUUID = generateUUID();
        const userData = data.data_user || data.data || {};
        const dataKaryawanAgen = data.data_karyawan || {
          id: userData.id,
          nik: userData.username,
          user_name: userData.user_name || usernameHybrid.trim(),
          nama_depan: userData.firstname || userData.name || 'AGEN',
          nama_belakang: userData.lastname || '',
          nama_lengkap: `${userData.firstname || ''} ${userData.lastname || ''}`.trim() || userData.name || 'AGEN',
          email: userData.email,
          alamat: userData.address,
          telepon: userData.phone || userData.nohp,
          jabatan: userData.jabatan || 'Pick-up',
          kantor: userData.kantor || `JNE ${userData.lastname || userData.firstname || 'Cilacap'}`,
          departement: 'Agen',
          status: String(userData.active) === '1' ? 'Aktif' : 'Nonaktif',
          role: 'agen',
          is_agen: true,
          device_id: userData.device_id,
        };

        if (isResignKaryawan(dataKaryawanAgen, userData)) {
          Swal.fire({
            icon: 'error',
            title: 'Gagal!',
            text: '❌ Karyawan sudah resign.',
            confirmButtonText: 'OK',
            customClass: { confirmButton: 'btn btn-primary' },
          }).then(() => {
            bersihkanData();
            setLoading(false);
          });
          return;
        }

        // Simpan sesi agen
        const userObjToStore = { ...userData, uuid: newUUID, device_id: newUUID, is_agen: true };
        const karyawanObjToStore = { ...dataKaryawanAgen, device_id: newUUID, is_agen: true };
        localStorage.setItem('data_karyawan', JSON.stringify(karyawanObjToStore));
        localStorage.setItem('data_user', JSON.stringify(userObjToStore));
        localStorage.setItem('device_uuid', newUUID);
        localStorage.setItem('is_agen', 'true');
        fetchRiwayat().catch(() => { });

        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (pos) => {
              localStorage.setItem('lokasi_terdeteksi', JSON.stringify({
                nama_kantor: dataKaryawanAgen.kantor,
                latitude: pos.coords.latitude,
                longitude: pos.coords.longitude,
                waktu: new Date().toISOString()
              }));
            },
            () => { },
            { timeout: 5000 }
          );
        }

        setDataKaryawan(dataKaryawanAgen);
        setIsEnabled(true);
        setLoading(false);

        await Swal.fire({
          icon: 'success',
          title: 'Validasi Berhasil!',
          html: `
            <div style="text-align: left; font-size: 13px; line-height: 1.5; margin-top: 8px;">
              <div style="background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 8px; padding: 10px; margin-bottom: 10px;">
                <div style="font-size: 15px; font-weight: 700; color: #15803d;">
                  ${dataKaryawanAgen.nama_lengkap}
                </div>
                <div style="font-size: 12px; color: #166534; margin-top: 2px;">
                  Kode. Debitur: <strong>${dataKaryawanAgen.nik}</strong> | Hybrid: <strong>${dataKaryawanAgen.user_name}</strong>
                </div>
              </div>
              <table style="width: 100%; border-collapse: collapse; font-size: 12px;">
                <tr><td style="color: #64748b; padding: 3px 0; width: 35%;">Alamat</td><td style="color: #0f172a; padding: 3px 0;">: ${userData.address || '-'}</td></tr>
                <tr><td style="color: #64748b; padding: 3px 0;">No. HP / Phone</td><td style="color: #0f172a; padding: 3px 0;">: ${userData.phone || userData.nohp || '-'}</td></tr>
                <tr><td style="color: #64748b; padding: 3px 0;">Email</td><td style="color: #0f172a; padding: 3px 0;">: ${userData.email || '-'}</td></tr>
                <tr><td style="color: #64748b; padding: 3px 0;">Jabatan / Layanan</td><td style="color: #0f172a; padding: 3px 0;">: ${userData.jabatan || 'Pick-up'}</td></tr>
                <tr><td style="color: #64748b; padding: 3px 0;">Status Akun</td><td style="color: #16a34a; font-weight: 600; padding: 3px 0;">: ${String(userData.active) === '1' ? 'Aktif' : 'Nonaktif'}</td></tr>
              </table>
            </div>
          `,
          confirmButtonText: 'Buka Halaman Agen',
          customClass: { confirmButton: 'btn btn-primary' },
        });

        navigate('/agen');
        return;
      } catch (err) {
        console.error('Error saat validasi debitur:', err);
        const errMsg = err.response?.data?.message || err.message || 'Terjadi kesalahan saat menghubungi server.';
        Swal.fire({
          icon: 'error',
          title: 'Validasi Gagal!',
          text: `❌ ${errMsg}`,
          confirmButtonText: 'OK',
          customClass: { confirmButton: 'btn btn-primary' },
        }).then(() => {
          bersihkanData();
          setLoading(false);
        });
        return;
      }
    }

    // =========================================================================
    // PERCABANGAN 2: VALIDASI KARYAWAN EKSISTING (DIAWALI CXP)
    // =========================================================================
    setLoading(true);
    Swal.fire({
      title: 'Memvalidasi...',
      text: '🔄 Memvalidasi NIK ke server...',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const response = await axios.post('https://jne-cilacap.com/slip/public/api/cek-nik', { nik: inputVal });
      const data = response.data;
      console.log('Response dari server:', data);

      if (!data.valid || !data.message) {
        Swal.fire({
          icon: 'error',
          title: 'Gagal!',
          text: '❌ NIK tidak ditemukan.',
          confirmButtonText: 'OK',
          customClass: { confirmButton: 'btn btn-primary' },
        }).then(() => {
          bersihkanData();
          setLoading(false);
        });
        return;
      }

      if (isResignKaryawan(data.data_karyawan, data.data_user)) {
        Swal.fire({
          icon: 'error',
          title: 'Gagal!',
          text: '❌ Karyawan sudah resign.',
          confirmButtonText: 'OK',
          customClass: { confirmButton: 'btn btn-primary' },
        }).then(() => {
          bersihkanData();
          setLoading(false);
        });
        return;
      }

      const namaKaryawan = data.message;

      if (!navigator.geolocation) {
        Swal.fire({
          icon: 'error',
          title: 'Gagal!',
          text: '❌ Browser tidak mendukung geolocation.',
          confirmButtonText: 'OK',
          customClass: { confirmButton: 'btn btn-primary' },
        }).then(() => {
          bersihkanData();
          setLoading(false);
        });
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const userLat = pos.coords.latitude;
          const userLng = pos.coords.longitude;

          let ditemukan = false;
          let jarakTerdekat = Infinity;
          let kantorDekat = '';

          kantorLocations.forEach((k) => {
            const d = hitungJarakMeter(userLat, userLng, k.latitude, k.longitude);
            if (d <= radiusDiterima && d < jarakTerdekat) {
              ditemukan = true;
              jarakTerdekat = d;
              kantorDekat = k.name;
            }
          });

          if (ditemukan) {
            const newUUID = generateUUID();

            Swal.fire({
              icon: 'success',
              title: 'Sukses!',
              text: `✅ Hi, ${namaKaryawan}. Anda berada di area ${kantorDekat}.`,
              confirmButtonText: 'OK',
              customClass: { confirmButton: 'btn btn-primary' },
            });
            // Jangan langsung tampilkan Navbar Bottom sampai user update dan device_id di server sama dengan di localstorage
            setIsEnabled(false);

            localStorage.setItem('data_karyawan', JSON.stringify(data.data_karyawan));
            localStorage.setItem('data_user', JSON.stringify({ ...data.data_user, uuid: newUUID }));
            localStorage.setItem('device_uuid', newUUID); // Simpan secara mandiri agar mudah terlihat

            const dataLokasi = {
              nama_kantor: kantorDekat,
              latitude: userLat,
              longitude: userLng,
              jarak: jarakTerdekat,
              waktu: new Date().toISOString()
            };
            localStorage.setItem('lokasi_terdeteksi', JSON.stringify(dataLokasi));

            setDataKaryawan(data.data_karyawan);
            setKantorTerdekat(kantorDekat);
            setJarak(jarakTerdekat.toFixed(2));
          } else {
            Swal.fire({
              icon: 'error',
              title: 'Gagal!',
              text: `❌ Hi, ${namaKaryawan}, Mohon lakukan validasi di lingkungan kantor.`,
              confirmButtonText: 'OK',
              customClass: { confirmButton: 'btn btn-primary' },
            }).then(() => {
              bersihkanData();
              setKantorTerdekat('');
              setJarak(null);
            });
          }
          setLoading(false);
        },
        (err) => {
          console.error(err);
          Swal.fire({
            icon: 'error',
            title: 'Gagal!',
            text: '❌ Gagal mendapatkan lokasi. Aktifkan izin lokasi.',
            confirmButtonText: 'OK',
            customClass: { confirmButton: 'btn btn-primary' },
          }).then(() => {
            bersihkanData();
            setLoading(false);
          });
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } catch (err) {
      console.error('Error saat validasi:', err);
      Swal.fire({
        icon: 'error',
        title: 'Gagal!',
        text: '❌ Terjadi kesalahan saat menghubungi server.',
        confirmButtonText: 'OK',
        customClass: { confirmButton: 'btn btn-primary' },
      }).then(() => {
        bersihkanData();
        setLoading(false);
      });
    }
  };

  const bersihkanData = () => {
    localStorage.clear();
    setDataKaryawan(null);
    setKantorTerdekat('');
    setJarak(null);
    setIsEnabled(false); // Nonaktifkan tombol saat data dihapus
  };


  return (
    <div style={{
      padding: '0 20px 20px 20px',
      fontFamily: 'Arial',
      textAlign: 'center',
      background: "linear-gradient(180deg, #F5F9FF 0%, #E8F1FF 40%, #FFFFFF 100%)",
      minHeight: '100vh',
    }}>
      {/* HEADER TOP BAR (FIXED) */}
      <header style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 999,
        background: 'rgba(245, 249, 255, 0.96)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
        boxShadow: '0 2px 10px rgba(0, 48, 135, 0.05)',
        padding: '10px 20px',
        boxSizing: 'border-box',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          maxWidth: 450,
          margin: '0 auto',
          width: '100%',
          boxSizing: 'border-box',
        }}>
          {/* Foto Profil, Salam & Nama di Pojok Kiri */}
          {dataKaryawan ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left', minWidth: 0, flex: 1, paddingRight: 8 }}>
              <div style={{
                width: 46, height: 46, borderRadius: '50%', overflow: 'hidden',
                border: '2.5px solid #00aaff', boxShadow: '0 2px 8px rgba(0, 170, 255, 0.25)', flexShrink: 0
              }}>
                <img
                  src={dataKaryawan.avatar ? `https://jne-cilacap.com/slip/images/images/${dataKaryawan.avatar}` : 'https://t3.ftcdn.net/jpg/13/11/22/86/360_F_1311228699_YoiLc5aJ3RWz3uRfdEtlV0UYSQjqf7RW.jpg'}
                  alt="Avatar"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={(e) => {
                    e.target.src = 'https://t3.ftcdn.net/jpg/13/11/22/86/360_F_1311228699_YoiLc5aJ3RWz3uRfdEtlV0UYSQjqf7RW.jpg';
                  }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0, overflow: 'hidden' }}>
                {/* Salam berdasarkan jam */}
                <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b', lineHeight: 1.2 }}>
                  {(() => {
                    const h = new Date().getHours();
                    if (h >= 5 && h < 11) return '☀️ Selamat Pagi,';
                    if (h >= 11 && h < 15) return '🌤️ Selamat Siang,';
                    if (h >= 15 && h < 19) return '🌇 Selamat Sore,';
                    return '🌙 Selamat Malam,';
                  })()}
                </span>
                <span style={{
                  fontSize: 15,
                  fontWeight: 800,
                  color: '#003087',
                  lineHeight: 1.25,
                  letterSpacing: '-0.2px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {dataKaryawan.nama_depan} {dataKaryawan.nama_belakang}
                </span>
              </div>
            </div>
          ) : <div />}

          {/* IKON KANAN: CS WhatsApp / Hubungi IT + Sync */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>

            {/* Tombol Hubungi IT (animasi collapsible otomatis - selalu tampil sebelum & sesudah validasi) */}
            <SupportITButton />

            {/* Sync / Update button (hanya jika dataKaryawan / isEnabled) */}
            {(isEnabled || dataKaryawan) && (
              <div style={{ display: 'inline-block', position: 'relative' }}>
                <button
                  id="update-button"
                  onClick={clearCache}
                  title="Perbarui data"
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    position: 'relative',
                    display: 'block',
                  }}
                >
                  <div style={{
                    width: 38,
                    height: 38,
                    borderRadius: '50%',
                    backgroundColor: '#eff6ff',
                    border: '1.5px solid #93c5fd',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 6px rgba(37, 99, 235, 0.12)',
                    transition: 'transform 0.4s ease, box-shadow 0.2s ease',
                  }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'rotate(180deg)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.25)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'rotate(0deg)'; e.currentTarget.style.boxShadow = '0 2px 6px rgba(37, 99, 235, 0.12)'; }}
                  >
                    <i className="fas fa-sync-alt" style={{ fontSize: 16, color: '#2563eb' }}></i>
                  </div>
                </button>

                {/* BADGE update notif */}
                {showBadge && (
                  <span style={{
                    position: 'absolute',
                    top: -3,
                    right: -3,
                    background: '#ef4444',
                    color: 'white',
                    borderRadius: '50%',
                    width: 16,
                    height: 16,
                    fontSize: 10,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px solid #ffffff',
                    animation: 'pulse 2s infinite',
                    zIndex: 20
                  }}>
                    !
                  </span>
                )}

                {/* TOOLTIP */}
                <div className="update-tooltip" style={{
                  position: 'absolute',
                  top: '100%',
                  right: -20,
                  background: '#1a1a1a',
                  color: 'white',
                  padding: '10px 14px',
                  borderRadius: 10,
                  fontSize: 13,
                  whiteSpace: 'nowrap',
                  marginTop: 10,
                  boxShadow: '0 8px 25px rgba(0,0,0,0.6)',
                  opacity: 0,
                  visibility: 'hidden',
                  transition: 'all 0.3s',
                  zIndex: 30,
                  pointerEvents: 'none'
                }}>
                  <strong>Update baru!</strong><br />
                  <span style={{ fontSize: 12 }}>{latestMessage}</span>
                </div>
              </div>
            )}

            {(isEnabled || dataKaryawan) && <UpdateIntroTour />}
          </div>
        </div>
      </header>

      {/* Spacer agar konten di bawah tidak tertutup header fixed */}
      <div style={{ height: 68, marginBottom: 16 }} />

      {(!dataKaryawan || isEnabled) && (
        <div style={{ marginBottom: 20, textAlign: 'center' }}>
          {!dataKaryawan && (
            <>
              <img
                src="/kavi.png"
                alt="KAVI"
                style={{ width: 360, height: 340, objectFit: 'cover', borderRadius: 10, display: 'block', margin: '0 auto' }}
              />
              <h3 style={{
                color: '#00aaff', fontFamily: 'Orbitron, sans-serif', fontSize: 20, fontWeight: 'bold',
                marginTop: 10, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
              }}>
                PT. Kalisha Utama Ghani
              </h3>
              <FiturTickerBanner />
            </>
          )}
          {isEnabled && (
            <div
              style={{
                overflow: "hidden",
                width: "100%",
                maxWidth: 450,
                margin: '18px auto 0',
                display: "flex",
                justifyContent: "flex-start",
                minHeight: 52,
              }}
            >
              {(() => {
                const currentItem = activeInfoList[index % activeInfoList.length] || activeInfoList[0];
                return (
                  <div
                    style={{
                      width: '100%',
                      padding: "12px 18px",
                      borderRadius: 18,
                      background: currentItem.bg,
                      color: currentItem.color,
                      fontWeight: 600,
                      fontSize: 14,
                      display: "flex",
                      alignItems: "center",
                      gap: 12,

                      /* ANIMATION */
                      opacity: animating ? 0 : 1,
                      filter: animating ? "blur(6px)" : "blur(0)",
                      transform: animating
                        ? "translateY(-40%)"
                        : "translateY(0)",
                      transition:
                        "opacity 0.45s ease, transform 0.45s ease, filter 0.45s ease",
                    }}
                  >
                    <span style={{ fontSize: 18 }}>{currentItem.icon}</span>
                    <span>{currentItem.text}</span>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {dataKaryawan && (
        (dataKaryawan.is_agen || location.pathname === '/agen') ? (
          /* ── CARD KHUSUS DATA AGEN (DARI TABEL USERS) ── */
          <div style={{
            borderRadius: 24,
            boxShadow: '0 4px 18px rgba(2, 132, 199, 0.08)',
            border: '1px solid #bae6fd',
            maxWidth: 450,
            margin: '0 auto 24px',
            overflow: 'hidden',
            backgroundColor: '#f0f9ff',
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
            textAlign: 'left',
          }}>
            {/* Top Bar Agen */}
            <div style={{
              display: 'flex',
              width: '100%',
              height: 44,
              alignItems: 'stretch',
            }}>
              <div style={{
                backgroundColor: '#f0f9ff',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                paddingLeft: 14,
                paddingRight: 2,
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '4px 10px',
                  borderRadius: 20,
                  fontSize: 11,
                  fontWeight: 700,
                  backgroundColor: '#ffffff',
                  border: '1px solid #7dd3fc',
                  color: '#0284c7',
                }}>
                  <Store size={13} color="#0284c7" />
                  <span>AGEN JNE</span>
                </div>
              </div>

              <div style={{ width: 34, height: 44, flexShrink: 0 }}>
                <svg width="34" height="44" viewBox="0 0 34 44" fill="none" style={{ display: 'block' }}>
                  <rect width="34" height="44" fill="#f0f9ff" />
                  <path d="M 0 0 C 17 0, 17 44, 34 44 L 34 0 Z" fill="#ffffff" />
                </svg>
              </div>

              <div style={{
                flex: 1,
                backgroundColor: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                paddingRight: 14,
                gap: 7,
              }}>
                <StatusOffline compact karyawan={dataKaryawan} />
              </div>
            </div>

            {/* Main Header Agen */}
            <div style={{
              backgroundColor: '#f0f9ff',
              padding: '12px 16px 14px',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 46,
                  height: 46,
                  borderRadius: '50%',
                  backgroundColor: '#ffffff',
                  border: '2px solid #38bdf8',
                  boxShadow: '0 2px 8px rgba(14, 165, 233, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#0284c7',
                  flexShrink: 0,
                }}>
                  <Store size={22} color="#0284c7" />
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                  }}>
                    <h4 style={{
                      margin: 0,
                      fontSize: 15,
                      fontWeight: 800,
                      color: '#0f172a',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                      {dataKaryawan.nama_lengkap || `${dataKaryawan.nama_depan} ${dataKaryawan.nama_belakang}`}
                    </h4>
                    <span style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#0284c7',
                      backgroundColor: '#ffffff',
                      padding: '2px 8px',
                      borderRadius: 12,
                      border: '1px solid #7dd3fc',
                      flexShrink: 0,
                    }}>
                      {dataKaryawan.nik}
                    </span>
                  </div>

                  {/* Progress Line Track */}
                  <div style={{
                    width: '100%',
                    height: 4,
                    borderRadius: 2,
                    backgroundColor: '#bae6fd',
                    marginTop: 7,
                    marginBottom: 6,
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      width: '100%',
                      height: '100%',
                      borderRadius: 2,
                      backgroundColor: '#0284c7',
                    }} />
                  </div>

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: 11,
                    color: '#64748b',
                    gap: 6,
                  }}>
                    <span style={{ fontWeight: 600, color: '#0369a1' }}>
                      Hybrid: {dataKaryawan.user_name || '-'}
                    </span>
                    <span style={{ fontWeight: 600, color: '#16a34a' }}>
                      ● Unlock
                    </span>
                  </div>
                </div>
              </div>

              {/* Detail Info Agen - No HP, Email, Alamat */}
              <div style={{
                backgroundColor: '#ffffff',
                borderRadius: 14,
                padding: '10px 14px',
                border: '1px solid #e0f2fe',
                display: 'flex',
                flexDirection: 'column',
                gap: 7,
                fontSize: 12,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', fontSize: 11 }}>
                  <Phone size={13} color="#0284c7" style={{ flexShrink: 0 }} />
                  <span>No. HP: <strong style={{ color: '#334155' }}>{dataKaryawan.telepon || dataKaryawan.phone || dataKaryawan.no_hp || dataKaryawan.nohp || '-'}</strong></span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', fontSize: 11 }}>
                  <Mail size={13} color="#0284c7" style={{ flexShrink: 0 }} />
                  <span>Email: <strong style={{ color: '#334155' }}>{dataKaryawan.email || '-'}</strong></span>
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, color: '#64748b', fontSize: 11, borderTop: '1px dashed #e0f2fe', paddingTop: 6 }}>
                  <MapPin size={13} color="#0284c7" style={{ marginTop: 2, flexShrink: 0 }} />
                  <span style={{ lineHeight: 1.4 }}>
                    <strong style={{ color: '#334155' }}>{dataKaryawan.alamat || dataKaryawan.address || '-'}</strong>
                  </span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* ── CARD KARYAWAN EKSISTING ── */
          <div style={{
            borderRadius: 24,
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)',
            border: '1px solid #cbd5e1',
            maxWidth: 450,
            margin: '0 auto 24px',
            overflow: 'hidden',
            backgroundColor: '#f4fbf7',
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
            textAlign: 'left',
          }}>
            {/* ── Two-Tone Top Bar: Left Tab + S-Curve Connector + Right Section ── */}
            <div style={{
              display: 'flex',
              width: '100%',
              height: 44,
              alignItems: 'stretch',
            }}>
              {/* Left Tab with metallic/bodyBg */}
              <div style={{
                backgroundColor: '#f4fbf7',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                paddingLeft: 14,
                paddingRight: 2,
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '4px 10px',
                  borderRadius: 20,
                  fontSize: 11,
                  fontWeight: 600,
                  backgroundColor: '#ffffff',
                  border: '1px solid #cbd5e1',
                  color: '#334155',
                }}>
                  <Briefcase size={12} color="#003087" />
                  <span>Dept: {dataKaryawan.departement || '-'}</span>
                </div>

                {/* Mini Trend / Accent Circle */}
                <div style={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  backgroundColor: '#ffffff',
                  border: '1px solid #cbd5e1',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#64748b',
                }}>
                  <TrendingUp size={11} />
                </div>
              </div>

              {/* S-Curve SVG Connector */}
              <div style={{ width: 34, height: 44, flexShrink: 0 }}>
                <svg width="34" height="44" viewBox="0 0 34 44" fill="none" style={{ display: 'block' }}>
                  <rect width="34" height="44" fill="#f4fbf7" />
                  <path d="M 0 0 C 17 0, 17 44, 34 44 L 34 0 Z" fill="#ffffff" />
                </svg>
              </div>

              {/* Right Section with #ffffff */}
              <div style={{
                flex: 1,
                backgroundColor: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                paddingRight: 14,
                gap: 7,
              }}>
                <StatusOffline compact karyawan={dataKaryawan} />
              </div>
            </div>

            {/* ── Main Content: Icon + Title + Progress + Subtitle ── */}
            <div style={{
              backgroundColor: '#f4fbf7',
              padding: '10px 16px 12px',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}>
              {/* Circle Icon Badge */}
              <div style={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                backgroundColor: '#ffffff',
                border: '1px solid #cbd5e1',
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.06)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#003087',
                flexShrink: 0,
              }}>
                <UserCheck size={22} color="#003087" />
              </div>

              {/* Title, Subtitle, Progress Bar */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                }}>
                  <h4 style={{
                    margin: 0,
                    fontSize: 14,
                    fontWeight: 700,
                    color: '#0f172a',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    letterSpacing: '-0.2px',
                  }}>
                    {dataKaryawan.jabatan && dataKaryawan.level && String(dataKaryawan.jabatan).toLowerCase().includes(String(dataKaryawan.level).toLowerCase())
                      ? dataKaryawan.jabatan
                      : `${dataKaryawan.level || ''} ${dataKaryawan.jabatan || dataKaryawan.divisi || ''}`.trim()}
                  </h4>
                  <span style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: '#475569',
                    backgroundColor: '#ffffff',
                    padding: '2px 8px',
                    borderRadius: 12,
                    border: '1px solid #cbd5e1',
                    flexShrink: 0,
                  }}>
                    {dataKaryawan.nik || 'NIK'}
                  </span>
                </div>

                {/* Progress Line Track */}
                <div style={{
                  width: '100%',
                  height: 4,
                  borderRadius: 2,
                  backgroundColor: '#cbd5e1',
                  marginTop: 7,
                  marginBottom: 6,
                  overflow: 'hidden',
                }}>
                  <div style={{
                    width: '100%',
                    height: '100%',
                    borderRadius: 2,
                    backgroundColor: '#003087',
                  }} />
                </div>

                {/* Subtitle / Details */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: 11,
                  color: '#64748b',
                  gap: 6,
                }}>
                  <span style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    fontWeight: 500,
                    textTransform: 'capitalize',
                  }}>
                    {(() => {
                      try {
                        const k = JSON.parse(localStorage.getItem('data_karyawan') || '{}');
                        const s = k.status ?? k.status_karyawan ?? dataKaryawan?.status;
                        if (s === 1 || s === '1') return 'Aktif';
                        if (s) return String(s);
                      } catch { }
                      return dataKaryawan?.status || 'Aktif';
                    })()}
                  </span>
                  <span style={{
                    flexShrink: 0,
                    fontWeight: 600,
                    color: '#003087',
                  }}>
                    {(() => {
                      try {
                        const k = JSON.parse(localStorage.getItem('data_karyawan') || '{}');
                        const kantor = k.kantor || k.nama_kantor || dataKaryawan?.kantor || dataKaryawan?.nama_kantor;
                        if (kantor) {
                          return String(kantor).toLowerCase().startsWith('jne') ? kantor : `JNE ${kantor}`;
                        }
                        const lok = JSON.parse(localStorage.getItem('lokasi_terdeteksi') || '{}');
                        if (lok.nama_kantor) {
                          return String(lok.nama_kantor).toLowerCase().startsWith('jne') ? lok.nama_kantor : `JNE ${lok.nama_kantor}`;
                        }
                      } catch { }
                      return `JNE ${dataKaryawan?.kantor || 'Cilacap'}`;
                    })()}
                  </span>
                </div>
              </div>
            </div>

            {/* ── Bottom Section: Lokasi Status ── */}
            <div style={{
              backgroundColor: '#f4fbf7',
              padding: '0 14px 14px',
            }}>
              <div style={{
                backgroundColor: '#ffffff',
                borderRadius: 16,
                padding: '8px 10px',
                border: '1px solid #cbd5e1',
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.03)',
                display: 'flex',
                alignItems: 'center',
                overflowX: 'auto',
              }}>
                <LokasiStatus />
              </div>
            </div>
          </div>
        )
      )}

      {/* FORM VALIDASI */}
      {!dataKaryawan && !loading ? (
        <>
          <div
            style={{
              backgroundColor: '#e0f7ff',
              border: '1px solid #bae6fd',
              padding: '8px 14px',
              borderRadius: 14,
              margin: '0 auto 12px',
              textAlign: 'center',
              width: '100%',
              maxWidth: 450,
              boxSizing: 'border-box',
            }}
          >
            <p style={{ color: '#0369a1', fontWeight: 600, fontSize: 12, margin: 0, lineHeight: 1.35 }}>
              Untuk login: Masukkan NIK (diawali CXP) jika Karyawan, atau Kode. Debitur jika Anda Agen JNE
            </p>
            <p style={{ color: '#64748b', fontSize: 11, margin: '2px 0 0 0' }}>
              Lalu klik tombol Validasi 👇
            </p>
            <button
              type="button"
              onClick={() => setShowPetunjukLogin(true)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                marginTop: 8,
                padding: '6px 14px',
                backgroundColor: '#ffffff',
                border: '1px solid #38bdf8',
                borderRadius: 20,
                color: '#0284c7',
                fontSize: 11.5,
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 2px 6px rgba(2, 132, 199, 0.12)',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f0f9ff';
                e.currentTarget.style.borderColor = '#0284c7';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#ffffff';
                e.currentTarget.style.borderColor = '#38bdf8';
              }}
            >
              <span>📖</span>
              <span>Lihat Petunjuk NIK & Kode. Debitur</span>
            </button>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0,
              width: '100%',
              maxWidth: 450,
              margin: '0 auto 16px',
              boxSizing: 'border-box',
            }}
          >
            <input
              type="text"
              placeholder="NIK (CXP...) / Kode. Debitur"
              value={nik}
              onChange={(e) => {
                const value = e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
                setNik(value);
              }}
              maxLength={25}

              style={{
                flex: 1,
                fontSize: 16,
                padding: '12px 14px',
                border: '1px solid #00aaff',
                borderRight: 'none',
                borderTopLeftRadius: 6,
                borderBottomLeftRadius: 6,
                boxSizing: 'border-box',
                outline: 'none',
                transition: 'box-shadow 0.3s ease',
                textTransform: 'uppercase',
                minWidth: 0,
              }}
              onFocus={(e) =>
                (e.target.style.boxShadow = '0 0 6px rgba(0, 170, 255, 0.6)')
              }
              onBlur={(e) => (e.target.style.boxShadow = 'none')}
            />
            <button
              onClick={handleValidasiNIKdanLokasi}
              style={{
                backgroundColor: '#00aaff',
                color: '#fff',
                padding: '12px 16px',
                border: '1px solid #00aaff',
                borderTopRightRadius: 6,
                borderBottomRightRadius: 6,
                fontSize: 16,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                outline: 'none',
                transition: 'all 0.3s ease',
              }}
              onMouseOver={(e) => {
                e.target.style.boxShadow = '0 0 10px rgba(0, 170, 255, 0.8)';
                e.target.style.backgroundColor = '#0099e6';
              }}
              onMouseOut={(e) => {
                e.target.style.boxShadow = 'none';
                e.target.style.backgroundColor = '#00aaff';
              }}
            >
              Validasi
            </button>
          </div>

          <div style={{ textAlign: 'center', margin: '-8px auto 14px', fontSize: 11.5, color: '#64748b' }}>
            Validasi membutuhkan persetujuan data & izin perangkat.{' '}
            <button
              type="button"
              onClick={() => {
                setIsPrivacyMandatory(false);
                setShowPrivacyModal(true);
              }}
              style={{
                background: 'none',
                border: 'none',
                color: '#0284c7',
                fontWeight: 600,
                cursor: 'pointer',
                padding: 0,
                textDecoration: 'underline',
                fontSize: 11.5,
              }}
            >
              -
            </button>
          </div>

          {(status.includes('✅') || status.includes('❌')) && (
            <div
              style={{
                backgroundColor: '#e0f7ff',
                padding: 10,
                borderRadius: 10,
                margin: '0 auto 20px',
                textAlign: 'center',
                width: '90%',
                maxWidth: 420,
              }}
            >
              <p
                style={{
                  marginTop: 10,
                  color: status.includes('❌')
                    ? 'red'
                    : status.includes('✅')
                      ? 'green'
                      : 'gray',
                }}
              >
                {status}
              </p>
              {jarak && (
                <small style={{ color: '#555' }}>
                  Jarak ke kantor: {jarak} meter
                </small>
              )}
            </div>
          )}
        </>
      ) : loading ? (
        <div style={{
          backgroundColor: '#e0f7ff', padding: 10, borderRadius: 10, textAlign: 'center', marginBottom: 20
        }}>
          <p style={{ fontWeight: 'bold', color: '#003087' }}>Tunggu sebentar...</p>
          <p style={{ color: '#555' }}>Sedang mengambil data anda di database.</p>
        </div>
      ) : (
        <>
          {dataKaryawan && (
            <>
              <HomeMenus
                dataKaryawan={dataKaryawan}
                badgeOB={badgeOB}
                badgeTitipan={badgeTitipan}
                navigate={navigate}
              />
              <RiwayatNotifikasiCard navigate={navigate} />
            </>
          )}
        </>
      )}

      <div style={{ fontSize: 12, color: '#888', marginBottom: 18, textAlign: 'center' }}>
        <span>&copy; 2025 PT. Kalisha Utama Ghani. All rights reserved.</span>
        <br />
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
          <button
            type="button"
            onClick={() => {
              setIsPrivacyMandatory(false);
              setShowPrivacyModal(true);
            }}
            style={{
              border: 'none',
              background: 'none',
              color: '#0284c7',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              padding: '4px 6px',
              textDecoration: 'underline',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1,
            }}
          >
            Kebijakan Privasi
          </button>
          <span style={{ color: '#cbd5e1' }}>•</span>
          <button
            type="button"
            onClick={() => setShowVersionLog(true)}
            style={{
              border: 'none',
              background: 'none',
              color: '#216fdcff',
              fontSize: 12,
              fontWeight: 700,
              cursor: 'pointer',
              padding: '4px 6px',
              textDecoration: 'underline',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1,
            }}
          >
            V {currentVersion}
          </button>
        </div>
      </div>

      <VersionLogModal
        open={showVersionLog}
        onClose={() => setShowVersionLog(false)}
      />

      <PetunjukLoginModal
        open={showPetunjukLogin}
        onClose={() => setShowPetunjukLogin(false)}
      />

      <PrivacyPolicyModal
        open={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
        isMandatory={isPrivacyMandatory}
        userNik={nik}
        onAgree={() => {
          setShowPrivacyModal(false);
          handleValidasiNIKdanLokasi(true);
        }}
      />

      <BottomNav isEnabled={isEnabled} /> {/* Oper isEnabled sebagai prop */}
    </div>
  );
};

const SecurityOnlyRoute = ({ children, allowedRoles = [] }) => {
  let jabatan = '';
  try {
    const karyawan = JSON.parse(localStorage.getItem('data_karyawan') || '{}');
    jabatan = String(karyawan.jabatan || '').trim().toLowerCase();
  } catch {
    jabatan = '';
  }

  const normalizedJabatan = normalizeJabatan(jabatan);
  const allowed = allowedRoles.some((role) => {
    const normalizedRole = normalizeJabatan(role);
    return normalizedJabatan === normalizedRole || normalizedJabatan.includes(normalizedRole);
  });

  useEffect(() => {
    if (allowed) return;
    Swal.fire({
      icon: 'warning',
      title: 'Akses Ditolak',
      text: allowedRoles.length === 3
        ? 'Menu ini hanya dapat diakses oleh Security, Human Capital, atau IT.'
        : 'Menu ini hanya dapat diakses oleh Security, Human Capital, General Affair, Koordinator, atau Jr Supervisor.',
      confirmButtonText: 'Mengerti',
      confirmButtonColor: '#3085d6',
    });
  }, [allowed, allowedRoles]);

  return allowed ? children : <Navigate to="/" replace />;
};

const ATKPageRoute = ({ children }) => {
  let isFemale = false;
  try {
    const u = JSON.parse(localStorage.getItem('data_user') || '{}');
    const k = JSON.parse(localStorage.getItem('data_karyawan') || '{}');
    const g = (u.gender || u.jenis_kelamin || k.gender || k.jenis_kelamin || '').toString().trim().toLowerCase();
    isFemale = g === 'female' || g === 'perempuan' || g === 'wanita' || g === 'p';
  } catch {
    isFemale = false;
  }

  useEffect(() => {
    if (!isFemale) return;
    Swal.fire({
      icon: 'warning',
      title: 'Akses Dibatasi',
      text: 'Menu pemesanan ATK tidak dapat diakses oleh akun Anda.',
      confirmButtonText: 'Kembali',
      customClass: { confirmButton: 'btn btn-primary' },
    });
  }, [isFemale]);

  return !isFemale ? children : <Navigate to="/" replace />;
};

const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={
          <PageTransition><Home /></PageTransition>
        } />

        <Route path="/agen" element={
          <PageTransition><Home /></PageTransition>
        } />

        <Route path="/absen" element={
          <PageTransition><Absen /></PageTransition>
        } />

        <Route path="/absen/edit/:id" element={
          <PageTransition><AbsenEdit /></PageTransition>
        } />

        <Route path="/lapor" element={
          <PageTransition><Lapor /></PageTransition>
        } />

        <Route path="/lapor/scan" element={
          <PageTransition><LaporScanPage /></PageTransition>
        } />

        <Route path="/izin" element={
          <PageTransition><Izin /></PageTransition>
        } />

        <Route path="/izin/form" element={
          <PageTransition><IzinFormPage /></PageTransition>
        } />

        <Route path="/sakit/form" element={
          <PageTransition><SakitFormPage /></PageTransition>
        } />

        <Route path="/dinas/form" element={
          <PageTransition><DinasFormPage /></PageTransition>
        } />

        <Route path="/keuangan" element={
          <PageTransition><Keuangan /></PageTransition>
        } />

        <Route path="/keuangan/form" element={
          <PageTransition><KeuanganFormPage /></PageTransition>
        } />

        <Route path="/handover" element={
          <PageTransition><KeuanganFormPage /></PageTransition>
        } />

        <Route path="/handover/track" element={
          <HandoverTrackingPage />
        } />

        <Route path="/security" element={
          <SecurityOnlyRoute allowedRoles={['security', 'human capital', 'it']}><PageTransition><SecurityPage /></PageTransition></SecurityOnlyRoute>
        } />

        <Route path="/monitoring-bbm" element={
          <SecurityOnlyRoute allowedRoles={['security', 'human capital', 'general affair', 'it', 'koordinator', 'jr supervisor']}><PageTransition><MonitoringBbmPage /></PageTransition></SecurityOnlyRoute>
        } />

        <Route path="/checklist-kendaraan" element={
          <SecurityOnlyRoute allowedRoles={['security', 'human capital', 'general affair', 'it', 'koordinator', 'jr supervisor']}><PageTransition><ChecklistKendaraanPage /></PageTransition></SecurityOnlyRoute>
        } />

        <Route path="/selesai" element={
          <PageTransition><Selesai /></PageTransition>
        } />

        <Route path="/request-it" element={
          <PageTransition><RequestITPage /></PageTransition>
        } />

        <Route path="/atk-pemesanan" element={
          <ATKPageRoute><PageTransition><ATKPemesananPage /></PageTransition></ATKPageRoute>
        } />

        <Route path="/atk" element={<ATKPageRoute><Navigate to="/atk-pemesanan" replace /></ATKPageRoute>} />
      </Routes>
    </AnimatePresence>
  );
};

const App = () => {
  useEffect(() => {
    document.title = `KAVI V ${currentVersion}`;
  }, []);

  useEffect(() => {
    if (!navigator.geolocation) return undefined;

    let watchId = null;
    let syncTimer = null;
    let isProcessing = false;

    const getPendingMonitors = () => {
      const now = Date.now();
      const monitors = readJsonArray(LOCAL_LOCATION_MONITOR_KEY);
      const updated = monitors.map(item => {
        if (item.status === 'pending' && item.expires_at <= now) {
          return { ...item, status: 'completed', completed_at: now };
        }
        return item;
      });

      if (JSON.stringify(monitors) !== JSON.stringify(updated)) {
        localStorage.setItem(LOCAL_LOCATION_MONITOR_KEY, JSON.stringify(updated));
      }

      return updated.filter(item => item.status === 'pending' && item.expires_at > now);
    };

    const updateMonitor = (key, patch) => {
      const monitors = readJsonArray(LOCAL_LOCATION_MONITOR_KEY);
      const updated = monitors.map(item => item.key === key ? { ...item, ...patch } : item);
      localStorage.setItem(LOCAL_LOCATION_MONITOR_KEY, JSON.stringify(updated));
    };

    const invalidateMonitor = async (monitor, distance) => {
      const leftAt = Date.now();
      updateMonitor(monitor.key, {
        status: 'left_location',
        left_at: leftAt,
        left_distance: Math.round(distance),
        synced: false,
      });

      updateLocalInvalidAbsen(monitor, distance);

      try {
        await markInvalidOnServer(monitor);
        updateMonitor(monitor.key, {
          synced: true,
          synced_at: Date.now(),
        });

        Swal.fire({
          icon: 'warning',
          title: 'Absen Perlu Verifikasi',
          text: `Anda terdeteksi meninggalkan lokasi absen sebelum ${monitor.duration_label || '1 jam'}. Absensi ditandai tidak valid dan perlu verifikasi.`,
          timer: 4500,
          showConfirmButton: false,
        });
      } catch (e) {
        console.error('Gagal sinkron status keluar lokasi:', e);
        updateMonitor(monitor.key, {
          sync_error: e.message || 'Gagal sinkron ke server',
          last_sync_attempt: Date.now(),
        });
      }
    };

    const processPosition = async (position) => {
      if (isProcessing) return;
      isProcessing = true;

      try {
        const { latitude, longitude } = position.coords;
        const pending = getPendingMonitors();

        for (const monitor of pending) {
          const distance = calculateDistanceMeters(
            latitude,
            longitude,
            monitor.office_latitude,
            monitor.office_longitude
          );

          if (distance > (monitor.max_distance || LOCATION_STAY_MAX_DISTANCE_METERS)) {
            await invalidateMonitor(monitor, distance);
          }
        }
      } finally {
        isProcessing = false;
        syncWatchState();
      }
    };

    const retryUnsyncedInvalid = async () => {
      const monitors = readJsonArray(LOCAL_LOCATION_MONITOR_KEY);
      const unsynced = monitors.filter(item => item.status === 'left_location' && !item.synced);

      for (const monitor of unsynced) {
        try {
          await markInvalidOnServer(monitor);
          updateMonitor(monitor.key, {
            synced: true,
            synced_at: Date.now(),
            sync_error: null,
          });
        } catch (e) {
          updateMonitor(monitor.key, {
            sync_error: e.message || 'Gagal sinkron ke server',
            last_sync_attempt: Date.now(),
          });
        }
      }
    };

    function syncWatchState() {
      const hasPending = getPendingMonitors().length > 0;

      if (hasPending && watchId === null) {
        watchId = navigator.geolocation.watchPosition(
          processPosition,
          () => { },
          {
            enableHighAccuracy: true,
            timeout: 15000,
            maximumAge: 30000,
          }
        );
      }

      if (!hasPending && watchId !== null) {
        navigator.geolocation.clearWatch(watchId);
        watchId = null;
      }
    }

    syncWatchState();
    retryUnsyncedInvalid();

    const handleMonitorUpdated = () => {
      syncWatchState();
    };

    window.addEventListener('storage', handleMonitorUpdated);
    window.addEventListener('absenLocationMonitorUpdated', handleMonitorUpdated);
    syncTimer = setInterval(() => {
      syncWatchState();
      retryUnsyncedInvalid();
    }, 30000);

    return () => {
      window.removeEventListener('storage', handleMonitorUpdated);
      window.removeEventListener('absenLocationMonitorUpdated', handleMonitorUpdated);
      if (watchId !== null) navigator.geolocation.clearWatch(watchId);
      if (syncTimer) clearInterval(syncTimer);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const refreshHandoverBadge = async () => {
      try {
        const response = await fetch('https://jne-cilacap.com/slip/public/api/handovers');
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const result = await response.json();
        if (!cancelled && result.status === 'success') {
          writeHandovers(result.data);
          localStorage.setItem('data_titipan_last_fetch', String(Date.now()));
          window.dispatchEvent(new Event('badgeUpdate'));
        }
      } catch (error) {
        console.warn(`Badge handover menggunakan data cache: ${error?.message || error}`);
      }
    };

    refreshHandoverBadge();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Router>
      <AnimatedRoutes />
      <SpeedInsights />
    </Router>
  );
};

export default App;

